import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNamespacedTranslation } from './hooks/useNamespacedTranslation';
import { useUserStore } from './components/stores/userStore';
import { db } from './firebaseFirestore';

type LangOption = { code: string; value: string }

type LanguageSelectorProps = {
  onChange?: (event: SelectChangeEvent<unknown>) => void
  useValue?: boolean
  value?: string
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  onChange,
  useValue,
  value = ''
}) => {
  const { i18n, t } = useNamespacedTranslation(['profile', 'common']);
  const { user } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLanguage = value || i18n.language || 'en';
  const [availableLangs, setAvailableLangs] = useState<LangOption[]>([
    { code: 'en', value: 'English' },
    { code: 'es', value: 'Spanish' },
  ]);
  const [lang, setLang] = useState(useValue ? 'English' : 'en');

  // Fetch the language list in its own effect, keyed on the session uid: the document is only
  // readable with a Firebase session, so after a logout the first read can race the anonymous
  // re-login and fail. The built-in English/Spanish defaults are already usable, so a failure is
  // logged quietly (no error panel, no duplicate toasts) and retried when the session changes.
  useEffect(() => {
    let cancelled = false
    async function getLangs() {
      try {
        const querySnapshot = doc(db, 'app-configurations/languages');

        const docRef = await getDoc(querySnapshot);
        const kv = docRef.data() as Record<string, LangOption> | undefined;
        if (!kv || cancelled) return;

        setAvailableLangs(Object.keys(kv).map((k) => kv[k]));
      } catch (error) {
        console.warn('[LanguageSelector] Using default language list', error);
      }
    }

    getLangs();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    let language = currentLanguage;
    if (/.+-.+/.test(currentLanguage)) {
      const result = /(.+)-.+/.exec(currentLanguage)?.[1] || 'en';
      language = useValue
        ? availableLangs.find((x) => x.code === result)?.value || result
        : result;
    } else {
      language = useValue
        ? availableLangs.find((x) => x.code === currentLanguage)?.value ||
          currentLanguage
        : currentLanguage;
    }

    if (onChange) {
      setLang(user?.language || currentLanguage);
    } else {
      setLang(language);
    }
  }, [currentLanguage, useValue, user?.language]);

  const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
    if (onChange) {
      setLang(event.target.value as string);
      onChange(event);
    } else {
      const selectedLanguage = (event.target.value as string) || 'en';
      i18n.changeLanguage(selectedLanguage);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('lang', selectedLanguage);
        return next;
      }, { replace: true });
    }
  };

  return (
    <Box sx={{ minWidth: 120, marginTop: 2, width: '100%' }}>
      <FormControl fullWidth>
        <InputLabel id="language-selector-label">
          {t('language')}
        </InputLabel>
        <Select
          name="language"
          className="flex flex-column gap-10"
          label={t('language')}
          labelId="language-selector-label"
          value={lang}
          onChange={handleLanguageChange}
        >
          {availableLangs.map(({ code, value }, i) => (
            <MenuItem key={i} value={useValue ? value : code}>
              {t(value)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export { LanguageSelector };
export default LanguageSelector;
